import { isIsometricExercise } from '../utils/ExerciseUtils';

/**
 * Analisi avanzata del recupero e potenziale sovrallenamento
 * @param {Array} workouts - Array di allenamenti
 * @param {Array} sets - Array di serie
 * @param {Object} trainingAnalyzer - Riferimento a TrainingAnalyzer per metodi utili
 * @returns {Array} Suggerimenti per recupero
 */
export function analyzeRecoveryAndOvertraining(workouts, sets, trainingAnalyzer) {
  // Indicatori di potenziale sovrallenamento
  const indicators = {
    highFrequency: false,
    volumeIncrease: false,
    performanceDecrease: false,
    targetMusclesOverused: {},
    sameExerciseTooOften: [],
    deloadNeeded: false,
    
    // Campi per tracking
    overallRiskScore: 0,
    muscleGroupRecoveryStatus: {},
    exercisesWithRapidVolumeIncrease: [] 
  };
  
  // Analizza la frequenza di allenamento per gruppi muscolari
  if (workouts.length >= 4) {
    // Ordina gli allenamenti per data
    const sortedWorkouts = [...workouts].sort((a, b) => 
      new Date(a.data_allenamento) - new Date(b.data_allenamento)
    );
    
    // Calcola il tempo totale di allenamento nelle ultime due settimane
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    const recentWorkouts = sortedWorkouts.filter(w => 
      new Date(w.data_allenamento) >= twoWeeksAgo
    );
    
    // Se ci sono più di 10 allenamenti nelle ultime due settimane, potrebbe essere eccessivo
    if (recentWorkouts.length > 10) {
      indicators.deloadNeeded = true;
    }
    
    // Controlla se ci sono gruppi muscolari allenati troppo frequentemente
    const muscleGroupsByDate = {};
    const exercisesByDate = {};
    
    sets.forEach(set => {
      const date = new Date(set.timestamp || set.data_allenamento).toISOString().split('T')[0];
      
      // Traccia gruppi muscolari per data
      if (!muscleGroupsByDate[date]) {
        muscleGroupsByDate[date] = new Set();
      }
      if (set.gruppo_muscolare) {
        muscleGroupsByDate[date].add(set.gruppo_muscolare);
      }
      
      // Traccia esercizi per data
      if (!exercisesByDate[date]) {
        exercisesByDate[date] = new Set();
      }
      if (set.esercizio_nome) {
        exercisesByDate[date].add(set.esercizio_nome);
      }
    });
    
    // Verifica se gli stessi gruppi muscolari sono allenati troppo frequentemente
    const dates = Object.keys(muscleGroupsByDate).sort();
    for (let i = 1; i < dates.length; i++) {
      const currentDate = new Date(dates[i]);
      const prevDate = new Date(dates[i-1]);
      const daysBetween = Math.round((currentDate - prevDate) / (24 * 60 * 60 * 1000));
      
      // Se meno di 48 ore tra allenamenti dello stesso gruppo muscolare
      if (daysBetween < 2) {
        const currentGroups = muscleGroupsByDate[dates[i]];
        const prevGroups = muscleGroupsByDate[dates[i-1]];
        
        currentGroups.forEach(group => {
          if (prevGroups.has(group)) {
            indicators.targetMusclesOverused[group] = true;
            
            // Calcola il livello di affaticamento per questo gruppo muscolare
            if (trainingAnalyzer && typeof trainingAnalyzer.calculateMuscleGroupFatigue === 'function') {
              const fatigueScore = trainingAnalyzer.calculateMuscleGroupFatigue(sets, group);
              indicators.muscleGroupRecoveryStatus[group] = {
                fatigue: fatigueScore,
                recoveryNeeded: fatigueScore > 7,
                lastTraining: dates[i]
              };
            }
          }
        });
      }
    }
    
    // Verifica se gli stessi esercizi vengono eseguiti troppo frequentemente
    const exerciseFrequency = {};
    
    for (const date in exercisesByDate) {
      exercisesByDate[date].forEach(exercise => {
        if (!exerciseFrequency[exercise]) {
          exerciseFrequency[exercise] = 0;
        }
        exerciseFrequency[exercise]++;
      });
    }
    
    // Identifica esercizi che vengono eseguiti in più del 70% degli allenamenti
    const workoutCount = dates.length;
    for (const exercise in exerciseFrequency) {
      if (exerciseFrequency[exercise] / workoutCount > 0.7) {
        indicators.sameExerciseTooOften.push(exercise);
      }
    }
    
    // Se sono stati identificati gruppi muscolari sovrallenati
    indicators.highFrequency = Object.keys(indicators.targetMusclesOverused).length > 0;
  }
  
  // Analisi del volume e performance
  if (sets.length > 0 && workouts.length >= 3) {
    // Calcola il volume totale per le ultime sessioni
    const workoutVolumes = {};
    sets.forEach(set => {
      const workoutId = set.allenamento_id;
      const exerciseId = set.esercizio_id || set.scheda_esercizio_id;
      const exerciseName = set.esercizio_nome || 'Sconosciuto';
      const muscleGroup = set.gruppo_muscolare || 'Generale';
      
      if (!workoutId || !exerciseId) return; // Skip if missing key data
      
      if (!workoutVolumes[workoutId]) {
        workoutVolumes[workoutId] = { total: 0, byExercise: {} };
      }
      
      if (!workoutVolumes[workoutId].byExercise[exerciseId]) {
        workoutVolumes[workoutId].byExercise[exerciseId] = {
          volume: 0,
          name: exerciseName,
          muscleGroup: muscleGroup
        };
      }
      
      // Calcola il volume per questo set
      let setVolume = 0;
      if (isIsometricExercise(exerciseName) || parseFloat(set.peso) === 0) {
        setVolume = parseInt(set.ripetizioni) || 0;
      } else {
        setVolume = (parseFloat(set.peso) || 0) * (parseInt(set.ripetizioni) || 0);
      }
      
      // Aggiorna volumi
      workoutVolumes[workoutId].total += setVolume;
      workoutVolumes[workoutId].byExercise[exerciseId].volume += setVolume;
    });
    
    // Converti in array e ordina
    const volumeEntries = Object.entries(workoutVolumes)
      .map(([id, volumeData]) => {
        const workout = workouts.find(w => w.id == id);
        return {
          id,
          date: workout ? new Date(workout.data_allenamento) : new Date(0),
          volume: volumeData.total,
          exerciseVolumes: volumeData.byExercise
        };
      })
      .sort((a, b) => a.date - b.date);
    
    // Verifica incremento di volume troppo rapido
    if (volumeEntries.length >= 3) {
      const recentVolumes = volumeEntries.slice(-3);
      const oldestVolume = recentVolumes[0].volume;
      const newestVolume = recentVolumes[recentVolumes.length - 1].volume;
      
      // Se il volume è aumentato di più del 20% in breve tempo
      if (oldestVolume > 0 && (newestVolume - oldestVolume) / oldestVolume > 0.2) {
        indicators.volumeIncrease = true;
        
        // Identifica quali esercizi hanno avuto maggior incremento di volume
        const exerciseVolumeChanges = {};
        
        // Per ogni esercizio, calcola il cambiamento di volume
        const oldestWorkoutId = recentVolumes[0].id;
        const newestWorkoutId = recentVolumes[recentVolumes.length - 1].id;
        
        const oldestExercises = workoutVolumes[oldestWorkoutId]?.byExercise || {};
        const newestExercises = workoutVolumes[newestWorkoutId]?.byExercise || {};
        
        for (const exerciseId in newestExercises) {
          const newVolume = newestExercises[exerciseId].volume;
          const oldVolume = oldestExercises[exerciseId]?.volume || 0;
          
          if (oldVolume > 0) {
            const percentChange = ((newVolume - oldVolume) / oldVolume) * 100;
            
            if (percentChange > 30) {  // Aumento significativo (>30%)
              exerciseVolumeChanges[exerciseId] = {
                name: newestExercises[exerciseId].name,
                percentChange: percentChange.toFixed(1),
                oldVolume,
                newVolume
              };
              
              indicators.exercisesWithRapidVolumeIncrease.push({
                id: exerciseId,
                name: newestExercises[exerciseId].name,
                percentChange: percentChange.toFixed(1)
              });
            }
          }
        }
      }
    }
  }
  
  // Calcola un punteggio complessivo di rischio di sovrallenamento (0-10)
  let riskScore = 0;
  if (indicators.highFrequency) riskScore += 3;
  if (indicators.volumeIncrease) riskScore += 2;
  if (indicators.deloadNeeded) riskScore += 3;
  if (indicators.sameExerciseTooOften.length > 2) riskScore += 2;
  
  indicators.overallRiskScore = Math.min(10, riskScore);
  
  // Generazione dei suggerimenti di recupero
  const recoverySuggestions = [];
  
  // 1. Suggerimento per alta frequenza di allenamento
  if (indicators.highFrequency) {
    const overusedMuscles = Object.keys(indicators.targetMusclesOverused).join(', ');
    recoverySuggestions.push({
      type: 'recovery',
      title: 'Frequenza di allenamento elevata',
      description: `Stai allenando ${overusedMuscles} con frequenza troppo elevata. Aumenta il tempo di recupero tra gli allenamenti.`,
      icon: 'Clock',
      priority: 'high',
      actionable: true,
      suggestedAction: `Attendi almeno 48-72 ore prima di riallenare ${overusedMuscles}`,
      recommendations: [
        `Attendi almeno 48-72 ore prima di riallenare ${overusedMuscles}`,
        'Dividi meglio gli allenamenti per permettere il recupero muscolare'
      ]
    });
  }
  
  // 2. Suggerimento per incremento volume
  if (indicators.volumeIncrease) {
    const exercisesWithHighestIncrease = indicators.exercisesWithRapidVolumeIncrease
      .sort((a, b) => parseFloat(b.percentChange) - parseFloat(a.percentChange))
      .slice(0, 2);
      
    const exercisesList = exercisesWithHighestIncrease.length > 0 
      ? `Particolare attenzione a: ${exercisesWithHighestIncrease.map(e => e.name).join(', ')}`
      : '';
      
    recoverySuggestions.push({
      type: 'recovery',
      title: 'Incremento volume complessivo',
      description: `Il volume di allenamento totale è aumentato molto rapidamente. ${exercisesList} Considera una settimana di scarico per facilitare il recupero.`,
      icon: 'TrendingDown',
      priority: 'medium',
      actionable: true,
      suggestedAction: 'Riduci il volume totale del 40% per una settimana mantenendo l\'intensità',
      recommendations: [
        'Riduci il volume totale del 40% per una settimana',
        'Mantieni l\'intensità ma riduci il numero di serie'
      ]
    });
  }
  
  // 3. Suggerimento per deload
  if (indicators.deloadNeeded) {
    recoverySuggestions.push({
      type: 'deload',
      title: 'Settimana di scarico consigliata',
      description: 'Hai completato molti allenamenti negli ultimi 14 giorni. Una settimana di scarico potrebbe migliorare recupero e prestazioni future.',
      icon: 'Battery',
      priority: 'high',
      actionable: true,
      suggestedAction: 'Riduci il volume totale del 40-50% e aggiungi più giorni di riposo questa settimana',
      recommendations: [
        'Riduci il volume totale del 40-50%',
        'Mantieni l\'intensità ma dimezza il numero di serie',
        'Aggiungi più giorni di riposo completo'
      ]
    });
  }
  
  // 4. Suggerimento per variazione esercizi
  if (indicators.sameExerciseTooOften.length > 0) {
    recoverySuggestions.push({
      type: 'variation',
      title: 'Variazione esercizi consigliata',
      description: `Stai eseguendo ${indicators.sameExerciseTooOften.slice(0, 2).join(', ')} troppo frequentemente. Aggiungi più variazione.`,
      icon: 'RefreshCw',
      priority: 'medium',
      actionable: true,
      suggestedAction: `Sostituisci temporaneamente ${indicators.sameExerciseTooOften[0]} con una variante simile`,
      recommendations: [
        'Sostituisci temporaneamente questi esercizi con varianti simili',
        'La variazione previene sovrallenamento e stimola nuove fibre muscolari'
      ]
    });
  }
  
  // 5. Nuovo: Suggerimento basato sul punteggio complessivo di rischio
  if (indicators.overallRiskScore >= 7) {
    recoverySuggestions.push({
      type: 'recovery_urgent',
      title: 'Alto rischio di sovrallenamento',
      description: `Il tuo livello di rischio sovrallenamento è ${indicators.overallRiskScore}/10. Considera di diminuire significativamente l'intensità e il volume per 1-2 settimane.`,
      icon: 'AlertCircle',
      priority: 'critical',
      actionable: true,
      suggestedAction: 'Riduci il volume del 50% e inserisci una settimana di riposo attivo',
      recommendations: [
        'Riduci immediatamente il volume di allenamento del 50%',
        'Inserisci 3-4 giorni di riposo completo',
        'Considera attività di recupero attivo come yoga, camminata o nuoto leggero'
      ]
    });
  }
  
  return recoverySuggestions;
}

/**
 * Calcola l'affaticamento per gruppo muscolare specifico
 * @param {Array} sets - Array di serie
 * @param {string} muscleGroup - Gruppo muscolare da analizzare
 * @returns {number} Punteggio di affaticamento (0-10)
 */
export function calculateMuscleGroupFatigue(sets, muscleGroup) {
  // Filtra le serie per il gruppo muscolare specifico
  const muscleGroupSets = sets.filter(set => 
    set.gruppo_muscolare === muscleGroup
  );
  
  if (muscleGroupSets.length === 0) return 0;
  
  // Calcola volume totale
  const totalVolume = muscleGroupSets.reduce((sum, set) => {
    const reps = parseInt(set.ripetizioni) || 0;
    const weight = parseFloat(set.peso) || 0;
    return sum + (reps * weight);
  }, 0);
  
  // Calcola intensità media (in base al rapporto peso/1RM stimato)
  let intensitySum = 0;
  let validSets = 0;
  
  muscleGroupSets.forEach(set => {
    const reps = parseInt(set.ripetizioni) || 0;
    const weight = parseFloat(set.peso) || 0;
    if (reps > 0 && weight > 0) {
      // Stima 1RM per questa serie usando la formula di Brzycki
      const estimatedRM = weight / (1.0278 - 0.0278 * Math.min(reps, 20));
      if (estimatedRM > 0) {
        // Calcola intensità come percentuale del 1RM
        const intensity = weight / estimatedRM;
        intensitySum += intensity;
        validSets++;
      }
    }
  });
  
  const avgIntensity = validSets > 0 ? intensitySum / validSets : 0;
  
  // Calcola densità (concentrazione delle serie in un breve periodo)
  const timestamps = muscleGroupSets
    .map(set => new Date(set.timestamp || set.data_allenamento).getTime())
    .sort();
  
  let densityFactor = 0;
  if (timestamps.length > 1) {
    const timeSpan = timestamps[timestamps.length - 1] - timestamps[0];
    const avgTimeBetweenSets = timeSpan / (timestamps.length - 1);
    // Maggiore densità (minor tempo tra set) = maggiore affaticamento
    densityFactor = 10000 / (avgTimeBetweenSets + 1); // +1 per evitare divisione per zero
    densityFactor = Math.min(5, densityFactor); // Limita a max 5
  }
  
  // Formula di affaticamento: combinazione di volume, intensità e densità
  // Normalizza per avere un punteggio 0-10
  const volumeFactor = Math.min(5, totalVolume / 1000);
  const intensityFactor = avgIntensity * 10; // 0-1 diventa 0-10
  
  return Math.min(10, Math.round(
    (volumeFactor * 0.4) + 
    (intensityFactor * 0.4) + 
    (densityFactor * 0.2)
  ));
}